import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useWorkspace} from '../hooks/useWorkspace';
import {workspacePath} from '../services/paths.js';

const {
  FiChevronLeft,
  FiChevronRight,
  FiCheckSquare,
  FiFileText,
  FiArrowRight
} = FiIcons;

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_CHIPS_PER_DAY = 3;

export default function Calendar() {
  const state = useWorkspace();
  const navigate = useNavigate();

  const [activeDate, setActiveDate] = useState(() => new Date());
  const [selectedDayEvents, setSelectedDayEvents] = useState(null);

  const year = activeDate.getFullYear();
  const month = activeDate.getMonth(); // 0-indexed

  // Aggregate bot-produced dates: tasks (due dates) + plans (created/target).
  // No manual calendar entry exists anywhere — the calendar is a read-only
  // lens over records bots already produced (T10 spec).
  const events = [];

  (state.tasks || []).forEach((t) => {
    const dStr = t.due || t.due_date;
    if (dStr) {
      events.push({
        id: `task_${t.id}`,
        recordId: t.id,
        title: t.title,
        date: String(dStr).slice(0, 10),
        type: 'task',
        status: t.status,
        module: t.module || 'tasks',
        owner: t.owner
      });
    }
  });

  (state.plans || []).forEach((p) => {
    const dStr = p.created || p.due;
    if (dStr) {
      events.push({
        id: `plan_${p.id}`,
        recordId: p.id,
        title: p.title,
        date: String(dStr).slice(0, 10),
        type: 'plan',
        status: p.status,
        module: p.module || 'finance',
        owner: state.bots?.find((b) => b.id === p.botId)?.name || 'Lead'
      });
    }
  });

  // Monday-first month grid with leading/trailing overflow days.
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  const startDayIndex = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = lastDayOfMonth.getDate();
  const prevMonthLastDay = new Date(year, month, 0).getDate();

  const calendarDays = [];

  for (let i = startDayIndex - 1; i >= 0; i--) {
    calendarDays.push({
      dayNumber: prevMonthLastDay - i,
      isCurrentMonth: false,
      dateStr: '',
      events: []
    });
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  for (let d = 1; d <= daysInMonth; d++) {
    const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    calendarDays.push({
      dayNumber: d,
      isCurrentMonth: true,
      dateStr: dStr,
      isToday: dStr === todayStr,
      events: events.filter((e) => e.date === dStr)
    });
  }

  const remaining = (7 - (calendarDays.length % 7)) % 7;
  for (let d = 1; d <= remaining; d++) {
    calendarDays.push({
      dayNumber: d,
      isCurrentMonth: false,
      dateStr: '',
      events: []
    });
  }

  function handlePrevMonth() {
    setActiveDate(new Date(year, month - 1, 1));
    setSelectedDayEvents(null);
  }

  function handleNextMonth() {
    setActiveDate(new Date(year, month + 1, 1));
    setSelectedDayEvents(null);
  }

  function handleToday() {
    setActiveDate(new Date());
    setSelectedDayEvents(null);
  }

  // Every event click-through lands on the producing module screen where the
  // source record lives (build.md §10 module slugs — never hard-coded routes).
  function handleEventClick(ev) {
    navigate(workspacePath(ev.module, state));
  }

  const monthName = activeDate.toLocaleDateString('en-US', {month: 'long', year: 'numeric'});

  return (
    <div className="page">
      <header className="page-header inline spread">
        <div>
          <span className="eyebrow">Calendar</span>
          <h1>Every date your bots have produced, in one view.</h1>
          <p>Task due dates and plan milestones surfaced automatically from work already done — no manual entry.</p>
        </div>

        <div className="calendar-nav">
          <button type="button" className="button secondary compact" onClick={handleToday}>
            Today
          </button>
          <button type="button" className="icon-button" onClick={handlePrevMonth} title="Previous month" aria-label="Previous month">
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <span className="calendar-nav-month">{monthName}</span>
          <button type="button" className="icon-button" onClick={handleNextMonth} title="Next month" aria-label="Next month">
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </header>

      {selectedDayEvents && (
        <section className="panel calendar-day-detail">
          <div className="inline spread">
            <div>
              <span className="eyebrow">Milestones on {selectedDayEvents.dateStr}</span>
              <h3>{selectedDayEvents.events.length} scheduled item{selectedDayEvents.events.length > 1 ? 's' : ''}</h3>
            </div>
            <button type="button" className="button ghost compact" onClick={() => setSelectedDayEvents(null)}>
              Close
            </button>
          </div>
          <div className="calendar-day-detail-list">
            {selectedDayEvents.events.map((ev) => (
              <button
                key={ev.id}
                type="button"
                className="calendar-day-detail-row"
                onClick={() => handleEventClick(ev)}
              >
                <span className="inline">
                  <SafeIcon icon={ev.type === 'task' ? FiCheckSquare : FiFileText} />
                  <b>{ev.title}</b>
                  <span className={`status ${ev.status}`}>{ev.status}</span>
                </span>
                <span className="inline">
                  <small className="muted">{ev.module}</small>
                  <SafeIcon icon={FiArrowRight} />
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="panel calendar-grid-panel">
        <div className="calendar-week-header">
          {DAYS_OF_WEEK.map((day) => (
            <div key={day} className="calendar-week-day">{day}</div>
          ))}
        </div>

        <div className="calendar-days-grid">
          {calendarDays.map((cell, idx) => (
            <div
              key={idx}
              className={`calendar-day-cell ${cell.isCurrentMonth ? '' : 'other-month'} ${cell.isToday ? 'today' : ''}`}
              onClick={() => cell.events?.length && setSelectedDayEvents(cell)}
              role={cell.events?.length ? 'button' : undefined}
              tabIndex={cell.events?.length ? 0 : undefined}
              onKeyDown={(e) => {
                if (cell.events?.length && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setSelectedDayEvents(cell);
                }
              }}
            >
              <span className="calendar-day-number">{cell.dayNumber}</span>

              <div className="calendar-day-chips">
                {(cell.events || []).slice(0, MAX_CHIPS_PER_DAY).map((ev) => (
                  <button
                    key={ev.id}
                    type="button"
                    className={`calendar-event-chip ${ev.type}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(ev);
                    }}
                    title={`${ev.title} (${ev.type} · ${ev.status})`}
                  >
                    <SafeIcon icon={ev.type === 'task' ? FiCheckSquare : FiFileText} />
                    <span className="calendar-event-chip-label">{ev.title}</span>
                  </button>
                ))}

                {(cell.events || []).length > MAX_CHIPS_PER_DAY && (
                  <small className="calendar-day-more">
                    +{cell.events.length - MAX_CHIPS_PER_DAY} more
                  </small>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
